(function () {
  'use strict';

  function formatMoney(cents) {
    var amount = Math.round(cents / 100);
    return '$' + amount.toLocaleString('es-CL');
  }

  function toNumber(value) {
    if (typeof value !== 'string') return NaN;
    return parseFloat(value.replace(',', '.'));
  }

  // Automatic tolerance: 5cm added to EACH edge of a dimension (left+right for
  // ancho, top+bottom for alto), so +10cm total per dimension. Applied before
  // the m² math, so the displayed total already includes it — nothing extra
  // for the customer to add by hand.
  var TOLERANCE_METERS_PER_DIMENSION = 0.10;

  // Same close icon the theme already uses (snippets/icon-close.liquid) —
  // Liquid can't run inside this JS-built markup, so the SVG is inlined here
  // rather than re-rendered.
  var CLOSE_ICON_SVG =
    '<svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M15.625 4.375L4.375 15.625" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M15.625 15.625L4.375 4.375" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  var uid = 0;

  class TestimoniaWallpaperCalculator extends HTMLElement {
    connectedCallback() {
      this.variants = [];
      var dataEl = this.querySelector('[data-testimonia-wallpaper-variants]');
      if (dataEl) {
        try {
          this.variants = JSON.parse(dataEl.textContent);
        } catch (e) {
          this.variants = [];
        }
      }

      this.walls = [{ id: ++uid, ancho: '', alto: '' }];

      this.wallsEl = this.querySelector('[data-walls]');
      this.addWallBtn = this.querySelector('[data-add-wall]');
      this.totalM2El = this.querySelector('[data-total-m2]');
      this.totalPriceEl = this.querySelector('[data-total-price]');
      this.errorEl = this.querySelector('[data-error]');

      this.form = this.findScopedForm();
      this.variantInput = this.form ? this.form.querySelector('input[name="id"]') : null;
      this.submitBtn = this.form
        ? this.form.querySelector('[type="submit"], .product-form__submit')
        : null;

      this.addWallBtn.addEventListener('click', () => {
        this.walls.push({ id: ++uid, ancho: '', alto: '' });
        this.renderWalls();
      });

      // Only the numbers change on every keystroke — the wall cards
      // themselves (the <input> elements) are left alone. Rebuilding them via
      // innerHTML on every keystroke would replace the input the customer is
      // currently typing into, resetting focus and cursor position after
      // every single character.
      this.wallsEl.addEventListener('input', (e) => {
        var row = e.target.closest('[data-wall-id]');
        if (!row) return;
        var wall = this.getWall(row.getAttribute('data-wall-id'));
        if (!wall) return;
        if (e.target.hasAttribute('data-ancho')) wall.ancho = e.target.value;
        if (e.target.hasAttribute('data-alto')) wall.alto = e.target.value;
        this.updateTotals();
      });

      this.wallsEl.addEventListener('click', (e) => {
        var removeBtn = e.target.closest('[data-remove-wall]');
        if (!removeBtn) return;
        var row = removeBtn.closest('[data-wall-id]');
        if (!row) return;
        var id = row.getAttribute('data-wall-id');
        this.walls = this.walls.filter((w) => String(w.id) !== String(id));
        if (!this.walls.length) this.walls.push({ id: ++uid, ancho: '', alto: '' });
        this.renderWalls();
      });

      document.addEventListener('variant:change', () => this.updateTotals());

      if (this.variantInput) {
        new MutationObserver(() => this.updateTotals()).observe(this.variantInput, {
          attributes: true,
          attributeFilter: ['value'],
        });
      }

      if (this.form) {
        this.form.addEventListener('submit', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.addWallsToCart();
        });
      }

      this.renderWalls();
    }

    // The Testimonia PDP renders the buy box more than once (desktop column +
    // the mobile "floated_form" sticky bar), each with its own
    // form[action*="/cart/add"]. A plain document-wide querySelector would
    // always grab the FIRST form on the page — which can be an unrelated one
    // (e.g. the Mercado Pago installment simulator's form, which has no
    // submit button). Walk up from this instance and bind to the form that
    // lives in the same buy box.
    findScopedForm() {
      var node = this.parentElement;
      while (node) {
        var btn = node.querySelector(
          'form[action*="/cart/add"] [type="submit"], form[action*="/cart/add"] .product-form__submit'
        );
        if (btn) return btn.closest('form');
        node = node.parentElement;
      }
      return document.querySelector('form[action*="/cart/add"]');
    }

    getWall(id) {
      return this.walls.filter((w) => String(w.id) === String(id))[0];
    }

    getCurrentVariant() {
      var id = this.variantInput ? parseInt(this.variantInput.value, 10) : null;
      var found = this.variants.filter((v) => v.id === id)[0];
      return found || this.variants[0] || null;
    }

    // Measurements are entered in meters. Each dimension gets the automatic
    // +10cm tolerance (5cm/edge) before computing the area, then the result
    // is rounded UP to the next whole m² — that is the quantity charged, and
    // it is always shown before "Agregar al carrito" so the displayed total
    // and the cart total can never disagree.
    computeWall(wall, pricePerM2Cents) {
      var ancho = toNumber(wall.ancho);
      var alto = toNumber(wall.alto);
      var valid = isFinite(ancho) && isFinite(alto) && ancho > 0 && alto > 0;
      var anchoWithTolerance = valid ? ancho + TOLERANCE_METERS_PER_DIMENSION : 0;
      var altoWithTolerance = valid ? alto + TOLERANCE_METERS_PER_DIMENSION : 0;
      var rawM2 = valid ? anchoWithTolerance * altoWithTolerance : 0;
      var chargedM2 = valid ? Math.max(1, Math.ceil(rawM2)) : 0;
      return {
        valid: valid,
        rawM2: rawM2,
        chargedM2: chargedM2,
        priceCents: chargedM2 * pricePerM2Cents,
      };
    }

    // Full rebuild of the wall cards — only on mount, or when a wall is
    // added/removed. Never called from the 'input' handler; see updateTotals.
    renderWalls() {
      this.wallsEl.innerHTML = this.walls
        .map((wall, index) => {
          var anchoId = 'testimonia-wallpaper-ancho-' + wall.id;
          var altoId = 'testimonia-wallpaper-alto-' + wall.id;

          return (
            '<div class="testimonia-wallpaper-calculator__wall" data-wall-id="' + wall.id + '">' +
              '<div class="testimonia-wallpaper-calculator__wall-header">' +
                '<span>Muro ' + (index + 1) + ':</span>' +
                (this.walls.length > 1
                  ? '<button type="button" class="testimonia-wallpaper-calculator__remove" data-remove-wall aria-label="Eliminar muro ' +
                    (index + 1) +
                    '">' + CLOSE_ICON_SVG + '</button>'
                  : '') +
              '</div>' +
              '<div class="testimonia-wallpaper-calculator__fields">' +
                '<div class="field">' +
                  '<label class="visually-hidden" for="' + anchoId + '">Ancho metro</label>' +
                  '<input class="field__input" id="' + anchoId + '" type="text" inputmode="decimal" autocomplete="off" placeholder="Ancho metro" data-ancho value="' + wall.ancho + '">' +
                '</div>' +
                '<div class="field">' +
                  '<label class="visually-hidden" for="' + altoId + '">Alto metro</label>' +
                  '<input class="field__input" id="' + altoId + '" type="text" inputmode="decimal" autocomplete="off" placeholder="Alto metro" data-alto value="' + wall.alto + '">' +
                '</div>' +
              '</div>' +
            '</div>'
          );
        })
        .join('');

      this.updateTotals();
    }

    // Recomputes the overall totals — leaves every <input> node untouched so
    // focus and cursor position survive typing. Per-wall numbers are not
    // shown; only the combined "Metros cuadrados" / "Total" summary is.
    updateTotals() {
      var variant = this.getCurrentVariant();
      var pricePerM2Cents = variant ? variant.price : 0;

      var totals = { m2: 0, price: 0 };

      this.walls.forEach((wall) => {
        var computed = this.computeWall(wall, pricePerM2Cents);
        if (computed.valid) {
          totals.m2 += computed.chargedM2;
          totals.price += computed.priceCents;
        }
      });

      this.totalM2El.textContent = totals.m2 + ' m²';
      this.totalPriceEl.textContent = formatMoney(totals.price);
    }

    showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }

    addWallsToCart() {
      var variant = this.getCurrentVariant();
      if (!variant) {
        this.showError('Selecciona una opción antes de agregar al carrito.');
        return;
      }

      var items = [];
      var hasInvalid = false;

      this.walls.forEach((wall, index) => {
        var computed = this.computeWall(wall, variant.price);
        if (!computed.valid) {
          hasInvalid = true;
          return;
        }
        items.push({
          id: variant.id,
          quantity: computed.chargedM2,
          properties: {
            Muro: String(index + 1),
            'Ancho (m)': wall.ancho,
            'Alto (m)': wall.alto,
            'Metros cuadrados (con tolerancia)': computed.chargedM2 + ' m²',
          },
        });
      });

      if (hasInvalid || !items.length) {
        this.showError('Ingresa medidas válidas (mayores a cero) para cada muro.');
        return;
      }

      this.showError('');

      if (this.submitBtn) {
        this.submitBtn.setAttribute('aria-disabled', 'true');
        this.submitBtn.classList.add('loading');
      }

      var cartUrl = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart/add.js';

      fetch(cartUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items }),
      })
        .then((r) => r.json())
        .then((responseData) => {
          if (this.submitBtn) {
            this.submitBtn.removeAttribute('aria-disabled');
            this.submitBtn.classList.remove('loading');
          }

          if (responseData && responseData.status) {
            this.showError(responseData.description || 'No se pudo agregar al carrito.');
            return;
          }

          var cartDrawer = document.querySelector('cart-drawer');
          var cartNotification = document.querySelector('cart-notification');

          if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
            fetch(cartUrl.replace('add.js', '') + '?sections=cart-drawer,cart-icon-bubble')
              .then((r) => r.json())
              .then((sections) => cartDrawer.renderContents({ sections: sections }))
              .catch(() => {
                window.location.href = '/cart';
              });
          } else if (cartNotification && typeof cartNotification.renderContents === 'function') {
            cartNotification.renderContents(responseData);
          } else {
            window.location.href = '/cart';
          }
        })
        .catch((err) => {
          console.error('[Testimonia wallpaper calculator] Cart error:', err);
          if (this.submitBtn) {
            this.submitBtn.removeAttribute('aria-disabled');
            this.submitBtn.classList.remove('loading');
          }
          this.showError('No se pudo agregar al carrito. Intenta de nuevo.');
        });
    }
  }

  if (!customElements.get('testimonia-wallpaper-calculator')) {
    customElements.define('testimonia-wallpaper-calculator', TestimoniaWallpaperCalculator);
  }
})();
